import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {TuffexProductCatalogueRow} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexProductCatalogueRow";
import {
    buildOemPartCatalogueAdditionalColumnValues,
    createCatalogueRow,
    deleteCatalogueRow,
    countCatalogueRowsForTemplate,
    fetchCatalogueRowsForTemplate,
    catalogueFiltersWithIds,
    listCatalogueColumnValues,
    namedFiltersFromTextColumns,
    type CatalogueTextColumnFilters,
    findCatalogueRowByCode,
    loadTuffexOemPartCatalogue,
    readCatalogueTextValue,
    readCatalogueUuidValue,
    updateCatalogueRow,
    type TuffexOemPartColumnMapping,
} from "~/domainModels/ottokart/tuffexProductCatalogue";
import type {PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import type {ZodType} from "zod";
import {z as zod} from "zod";

export type OemPart = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    displayName: string;
    manufacturerId: Uuid;
    code: string;
};

export type PostOemPart = {
    createdBy: Uuid;
    updatedBy?: Uuid;
    displayName: string;
    manufacturerId: Uuid;
    code: string;
};

export type ListOemPartsRequest = {
    page?: number;
    size?: number;
    sort?: string;
    manufacturer?: Uuid;
};

export const oemPartZodValidator: ZodType<OemPart> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    displayName: zod.string(),
    manufacturerId: uuidZodValidator,
    code: zod.string(),
});

export const postOemPartZodValidator: ZodType<PostOemPart> = zod.object({
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator.optional(),
    displayName: zod.string(),
    manufacturerId: uuidZodValidator,
    code: zod.string(),
});

export const listOemPartsRequestZodValidator: ZodType<ListOemPartsRequest> = zod.object({
    page: zod.number().optional(),
    size: zod.number().optional(),
    sort: zod.string().optional(),
    manufacturer: uuidZodValidator.optional(),
});

export function mapOemPartCatalogueRowToOemPart(
    catalogueRow: TuffexProductCatalogueRow,
    columnMapping: TuffexOemPartColumnMapping,
): OemPart {
    const code = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.codeColumnId);
    const displayName = readCatalogueTextValue(catalogueRow.additionalColumnValues, columnMapping.displayNameColumnId);
    const manufacturerId = readCatalogueUuidValue(
        catalogueRow.additionalColumnValues,
        columnMapping.manufacturerIdColumnId,
    );

    return {
        id: catalogueRow.id,
        createdAt: catalogueRow.createdAt,
        updatedAt: catalogueRow.updatedAt,
        createdBy: catalogueRow.createdBy,
        updatedBy: catalogueRow.updatedBy,
        displayName: displayName.length > 0 ? displayName : code,
        manufacturerId: manufacturerId ?? ("00000000-0000-4000-8000-000000000001" as Uuid),
        code: code,
    };
}

export async function listOemParts({
    accessToken,
    filters,
    limit,
    listParams,
    offset,
    search,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: CatalogueTextColumnFilters;
    limit?: number;
    listParams?: ListOemPartsRequest;
    offset?: number;
    search?: string;
}): Promise<Result<Array<OemPart>>> {
    const catalogueContextResult = await loadTuffexOemPartCatalogue({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: catalogueContextResult.data.oemPartColumns.displayNameColumnId,
            filters: catalogueFiltersWithIds({
                columnIds: [
                    {columnId: catalogueContextResult.data.oemPartColumns.manufacturerIdColumnId, values: filters?.manufacturerIds},
                ],
                filters: filters,
            }),
            slugColumnId: null,
        }),
        ...(limit !== undefined && offset !== undefined
            ? {
                  limit: limit,
                  offset: offset,
              }
            : {}),
        search: search,
        searchColumnIds: [catalogueContextResult.data.oemPartColumns.displayNameColumnId, catalogueContextResult.data.oemPartColumns.codeColumnId].filter((columnId): columnId is Uuid => columnId !== null),
        workspaceId: workspaceId,
        productCatalogueTemplateId: catalogueContextResult.data.oemPartColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const columnMapping = catalogueContextResult.data.oemPartColumns;
    let oemParts = rowsResult.data.map((catalogueRow) => {
        return mapOemPartCatalogueRowToOemPart(catalogueRow, columnMapping);
    });

    if (listParams?.manufacturer !== undefined) {
        oemParts = oemParts.filter((oemPart) => {
            return oemPart.manufacturerId === listParams.manufacturer;
        });
    }

    return dataResult(oemParts);
}

export async function countOemParts({
    filters,
    search,
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: CatalogueTextColumnFilters;
    search?: string;
}): Promise<Result<number>> {
    const catalogueContextResult = await loadTuffexOemPartCatalogue({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }
    return countCatalogueRowsForTemplate({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        namedFilters: namedFiltersFromTextColumns({
            displayNameColumnId: catalogueContextResult.data.oemPartColumns.displayNameColumnId,
            filters: catalogueFiltersWithIds({
                columnIds: [
                    {columnId: catalogueContextResult.data.oemPartColumns.manufacturerIdColumnId, values: filters?.manufacturerIds},
                ],
                filters: filters,
            }),
            slugColumnId: null,
        }),
        productCatalogueTemplateId: catalogueContextResult.data.oemPartColumns.templateId,
        search: search,
        searchColumnIds: [catalogueContextResult.data.oemPartColumns.displayNameColumnId, catalogueContextResult.data.oemPartColumns.codeColumnId].filter((columnId): columnId is Uuid => columnId !== null),
        workspaceId: workspaceId,
    });
}

export async function listOemPartFilterColumnValues(context: PlutoWorkspaceClientContext): Promise<Result<{displayName: Array<string>; manufacturerId: Array<string>}>> {
    const catalogueContextResult = await loadTuffexOemPartCatalogue(context);
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }
    const columns = catalogueContextResult.data.oemPartColumns;
    const displayNameResult = await listCatalogueColumnValues({
        ...context,
        columnId: columns.displayNameColumnId,
        templateId: columns.templateId,
    });
    if (displayNameResult.success !== true) {
        return displayNameResult;
    }
    const manufacturerResult = await listCatalogueColumnValues({
        ...context,
        columnId: columns.manufacturerIdColumnId,
        templateId: columns.templateId,
    });
    if (manufacturerResult.success !== true) {
        return manufacturerResult;
    }
    return dataResult({
        displayName: displayNameResult.data,
        manufacturerId: manufacturerResult.data,
    });
}

export async function getOemPartById({
    accessToken,
    oemPartId,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    oemPartId: string;
}): Promise<Result<OemPart>> {
    const listResult = await listOemParts({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (listResult.success !== true) {
        return listResult;
    }

    const oemPart = listResult.data.find((entry) => {
        return entry.id === oemPartId;
    });
    if (oemPart === undefined) {
        return errorResult(new Error(`OEM part catalogue row not found: ${oemPartId}`));
    }

    return dataResult(oemPart);
}

export async function createOemPart({
    accessToken,
    oemPart,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    oemPart: PostOemPart;
}): Promise<Result<Uuid>> {
    const catalogueContextResult = await loadTuffexOemPartCatalogue({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const columnMapping = catalogueContextResult.data.oemPartColumns;
    const code = oemPart.code.trim();
    if (code.length === 0) {
        return errorResult(new Error("OEM part code is required."));
    }

    const createResult = await createCatalogueRow({
        accessToken: accessToken,
        additionalColumnValues: buildOemPartCatalogueAdditionalColumnValues({
            code: code,
            columnMapping: columnMapping,
            displayName: oemPart.displayName.trim().length > 0 ? oemPart.displayName.trim() : code,
            manufacturerId: oemPart.manufacturerId,
        }),
        parentId: null,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueTemplateId: columnMapping.templateId,
        workspaceId: workspaceId,
    });
    if (createResult.success !== true) {
        return createResult;
    }

    const createdRow = await findCatalogueRowByCode({
        accessToken: accessToken,
        code: code,
        columnMapping: columnMapping,
        intellsysBaseUrl: intellsysBaseUrl,
        productCatalogueTemplateId: columnMapping.templateId,
        workspaceId: workspaceId,
    });
    if (createdRow === null) {
        return errorResult(new Error(`Created OEM part catalogue row could not be found for code: ${code}`));
    }

    return dataResult(createdRow.id);
}

export async function updateOemPart({
    accessToken,
    oemPart,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    oemPart: OemPart;
}): Promise<Result<string>> {
    const catalogueContextResult = await loadTuffexOemPartCatalogue({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        productCatalogueTemplateId: catalogueContextResult.data.oemPartColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === oemPart.id;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`OEM part catalogue row not found: ${oemPart.id}`));
    }

    const columnMapping = catalogueContextResult.data.oemPartColumns;
    const code = oemPart.code.trim();
    if (code.length === 0) {
        return errorResult(new Error("OEM part code is required."));
    }

    const updateResult = await updateCatalogueRow({
        accessToken: accessToken,
        additionalColumnValues: buildOemPartCatalogueAdditionalColumnValues({
            code: code,
            columnMapping: columnMapping,
            displayName: oemPart.displayName.trim().length > 0 ? oemPart.displayName.trim() : code,
            manufacturerId: oemPart.manufacturerId,
        }),
        catalogueRow: catalogueRow,
        parentId: catalogueRow.parentId,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (updateResult.success !== true) {
        return updateResult;
    }

    return dataResult(oemPart.id);
}

export async function deleteOemPartById({
    accessToken,
    oemPartId,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    oemPartId: string;
}): Promise<Result<string>> {
    const catalogueContextResult = await loadTuffexOemPartCatalogue({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (catalogueContextResult.success !== true) {
        return catalogueContextResult;
    }

    const rowsResult = await fetchCatalogueRowsForTemplate({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        productCatalogueTemplateId: catalogueContextResult.data.oemPartColumns.templateId,
    });
    if (rowsResult.success !== true) {
        return rowsResult;
    }

    const catalogueRow = rowsResult.data.find((row) => {
        return row.id === oemPartId;
    });
    if (catalogueRow === undefined) {
        return errorResult(new Error(`OEM part catalogue row not found: ${oemPartId}`));
    }

    const deleteResult = await deleteCatalogueRow({
        accessToken: accessToken,
        catalogueRow: catalogueRow,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    if (deleteResult.success !== true) {
        return deleteResult;
    }

    return dataResult(oemPartId);
}

export async function buildOemPartByIdMap(
    context: PlutoWorkspaceClientContext,
): Promise<Result<Map<Uuid, OemPart>>> {
    const listResult = await listOemParts(context);
    if (listResult.success !== true) {
        return listResult;
    }

    const oemPartById = new Map<Uuid, OemPart>();
    for (const oemPart of listResult.data) {
        oemPartById.set(oemPart.id, oemPart);
    }

    return dataResult(oemPartById);
}
